import Utilities from '../TS/Overrides/Utilities';
import FlowStep from './Enums/Flowstep';
import IdentificationType from './Enums/identificationType';
import ConditionalClickPlacement from './Enums/ConditionalClickPlacement';
import Logger from './Overrides/Logger';
import Events from './Enums/Events';
import { isFailureResponse, isSuccessResponse, isSuccessWithConditionResponse } from './Guards/FlowBaseGuards';
import IdentifyStatus from './Enums/IdentifyStatus';
export class FlowBase {
    logIdentifyStep(step, detail = {}, level = 'info') {
        var _a;
        const now = Date.now();
        const payload = Object.assign(Object.assign({}, detail), { step, timestamp: now, elapsed_since_identify_ms: this.identifyStartedAt ? now - this.identifyStartedAt : null, flow_name: this.name, page_url: window.location.href, user_agent: window.navigator.userAgent, integrator_base_url: ((_a = this.integrator) === null || _a === void 0 ? void 0 : _a.baseUrl) || null, integrator_identify_url: this.integratorIdentifyUrl || null });
        if (level === 'error') {
            Logger.error(`${step}`, payload);
        }
        else if (level === 'warn') {
            Logger.warn(`${step}`, payload);
        }
        else {
            Logger.debug(`${step}`, payload);
        }
    }
    getExpectedIdentifyOrigins() {
        var _a;
        const urls = [this.integratorIdentifyUrl, (_a = this.integrator) === null || _a === void 0 ? void 0 : _a.baseUrl];
        const origins = urls
            .filter((value) => typeof value === 'string' && value.length > 0)
            .map((value) => {
            try {
                return new URL(value, window.location.href).origin;
            }
            catch (error) {
                return null;
            }
        })
            .filter((value) => typeof value === 'string' && value.length > 0);
        return Array.from(new Set(origins));
    }
    cleanupIdentifyMessageHandler() {
        if (this.identifyMessageHandler) {
            window.removeEventListener('message', this.identifyMessageHandler);
            this.identifyMessageHandler = null;
        }
    }
    parseIframeMessageData(event) {
        if (typeof event.data === 'string') {
            try {
                return JSON.parse(event.data);
            }
            catch (error) {
                this.logIdentifyStep('identify_message_parse_error', {
                    origin: event.origin,
                    raw_message: event.data,
                    error_message: error instanceof Error ? error.message : String(error)
                }, 'warn');
                return null;
            }
        }
        if (event.data && typeof event.data === 'object') {
            return event.data;
        }
        return null;
    }
    constructor(name, identification, args) {
        this.identificationMethods = {
            [IdentificationType.IFRAME]: this.iframeIdentify.bind(this),
            [IdentificationType.AJAX]: this.ajaxIdentify.bind(this),
            [IdentificationType.PREID]: this.preIdentify.bind(this)
        };
        this.metadata = {}; //Change to any until page-attributes(Meta data fields are generated and stored from page attribuest such as page_attributes-subscribe_landing_page) problem can be resolved
        this.identification = null;
        this.territory = '';
        this.integratorIdentifyUrl = '';
        this.internalCall = false;
        this.timeoutLimit = 180000; // 3 minutes
        this.plan = '';
        this.productSlug = '';
        this.otpChallengeValidateURL = 'subscribe'; //The new contract states that otpChallengeValidateURL should be the same as subscribeEndpoint
        this.optChallengeSubmitURL = 'otp/identify';
        this.identified = false;
        this.trackConversions = false;
        this.identifyCallFinished = false;
        this.redirecting = false;
        this.subscribeEndpoint = 'subscribe';
        this.identifyEndpoint = 'lp/he/identify';
        this.identity = '';
        this.productUrl = '';
        this.locale = '';
        this.integratorIdentity = '';
        this.skipElementCheck = false;
        this.identifyTimeout = 10;
        this.consentTickbox = false;
        this.consentTickboxAutofilled = false;
        this.inactiveTimeout = false;
        this.conditionalClickPlacement = null;
        this.conditionalClick = false;
        this.subscribeClick = false;
        this.OTP = false;
        this.preSubscribe = true; //Pre subscribe is true by default
        this.timeout = 0;
        this.iframeTimeout = 0;
        this.identifyMessageHandler = null;
        this.identifyStartedAt = 0;
        this.identifyFirstMessageAt = 0;
        this.token = '';
        this.step = '';
        this.fraud_detection_id = '';
        this.mixpanel_tracking_id = '';
        this.identifyBackupCalled = false;
        //check class has been setup correctly
        if (!(Object.values(IdentificationType).includes(identification))) {
            throw new Error('Identification method not found.');
        }
        this.name = name;
        this.identification = identification;
        this.args = args;
    }
    //in LP.ts the components are created and set up using this non-overidable function
    setupFlowComponents(uiController, tracker, integrator, dispatcher, genericEvents) {
        this.uiControl = uiController;
        this.tracker = tracker;
        this.integrator = integrator;
        this.dispatcher = dispatcher;
        this.genericEvents = genericEvents;
    }
    //in LP.ts the class properties are overriden and set up using this non-overidable function
    setupBaseData(args) {
        this.handleArgs(args);
    }
    handleArgs(args) {
        for (const [key, value] of Object.entries(args)) {
            //this.args is already typed so we know the values being passed are safe
            //this would have just been an extra layer of pre-caution
            //@ts-ignore TS Community decided this should remain any which causes error here
            this[key] = value;
        }
    }
    overwriteClassProps() {
        if (this.metadata.fraud_detection_id) {
            this.fraud_detection_id = this.metadata.fraud_detection_id;
        }
        if (this.metadata.event_tracking_id) {
            this.mixpanel_tracking_id = this.metadata.event_tracking_id;
        }
        this.handleArgs(this.args);
    }
    returnRequiredElements() {
        return [
            {
                elem: this.uiControl.controls.spinner,
                error: "Spinner not set. "
            },
            {
                elem: this.uiControl.controls.subscribe,
                error: "Subscribe not set. ",
                additionalCheck: this.subscribeClick,
            },
            {
                elem: this.uiControl.controls.confirm,
                error: "Confirm not set. "
            },
            {
                elem: this.uiControl.controls.otp.error,
                error: "OTP Error not set. ",
                additionalCheck: this.OTP
            },
            {
                elem: this.uiControl.controls.otp.container,
                error: "OTP Container not set. ",
                additionalCheck: this.OTP
            },
            {
                elem: this.uiControl.controls.otp.challenge,
                error: "OTP Challenge not set. ",
                additionalCheck: this.OTP
            },
            {
                elem: this.uiControl.controls.otp.validate,
                error: "OTP Validate not set. ",
                additionalCheck: this.OTP
            },
            {
                elem: this.uiControl.controls.otp.msisdn,
                error: "OTP MSISDN not set. ",
                additionalCheck: this.OTP
            },
            {
                elem: this.uiControl.controls.conditionalClick.button,
                error: "Conditional click button not set. ",
                additionalCheck: this.conditionalClick
            },
            {
                elem: this.uiControl.controls.conditionalClick.container,
                error: "Conditional click container not set. ",
                additionalCheck: this.conditionalClick
            },
            {
                elem: this.uiControl.controls.consent.consentTickbox,
                error: "Consent tickbox not set. ",
                additionalCheck: this.consentTickbox
            },
            {
                elem: this.uiControl.controls.consent.consentTickboxMessage,
                error: "Consent tickbox message not set. ",
                additionalCheck: this.consentTickbox
            },
        ];
    }
    //This is a continuation from the constructor after LP.js has added all the modules
    preInit() {
        this.overwriteClassProps();
        //check all of the UI elements have been bound otherwise throw an error
        const elementsToCheck = this.returnRequiredElements();
        let errString = '';
        elementsToCheck.forEach(elm => {
            if (("additionalCheck" in elm && elm.additionalCheck && !elm.elem) ||
                (!("additionalCheck" in elm) && !elm.elem)) {
                errString += elm.error;
            }
        });
        if (errString != '' && !this.skipElementCheck) {
            throw new Error(errString);
        }
        if (this.conditionalClick && this.conditionalClickPlacement === ConditionalClickPlacement.SECOND && !this.subscribeClick) {
            throw new Error('Conditional Click can not be second if subscribe click is disabled. That means that confirm would be shown and then conditional click but confirm must always be last.');
        }
    }
    overridableInit() {
        //This is a pure abstract function for the user to override not to be used by this class
    }
    init() {
        this.preInit();
        this.overridableInit();
        this.integrator.appendBaseData({ territory: this.territory });
        Logger.debug(`Flow (${this.name}): initialising flow`);
        this.bindClickEvents();
        this.bindTrackingEvents();
        this.showFirstStep();
        this.identify();
        this.setInactiveTimeout();
        this.handleConsentClick();
    }
    handleConsentClick() {
        if (this.consentTickbox && this.uiControl.controls.consent.consentTickbox) {
            const checkbox = this.uiControl.controls.consent.consentTickbox;
            checkbox.checked = this.consentTickboxAutofilled;
        }
    }
    setInactiveTimeout() {
        if (this.inactiveTimeout) {
            this.clearInactiveTimeout();
            this.timeout = window.setTimeout(() => {
                this.handleSetPageStateTimeout();
            }, this.timeoutLimit);
            Logger.debug(`Flow (${this.name}): set inactive timeout ${this.timeout}`);
        }
    }
    clearInactiveTimeout() {
        if (this.timeout && this.inactiveTimeout) {
            Logger.debug(`Flow (${this.name}): clearing timeout ${this.timeout}`);
            window.clearTimeout(this.timeout);
            this.timeout = 0;
        }
    }
    handleSetPageStateTimeout() {
        if (this.inactiveTimeout) {
            Logger.debug(`Flow (${this.name}): handling inactive timeout`);
            this.setFlowStep(FlowStep.TIMEOUT);
            this.tracker.track(Events.PAGE_TIMEOUT, {});
            this.uiControl.showErrorMessage('SESSION_TIMEOUT_ERROR', 'Retry');
        }
    }
    identify() {
        if (this.identification) {
            this.identificationMethods[this.identification].bind(this)();
        }
    } // Not overridable, configurable based on type of ID. if successful, show subscribe step, if not, go to backup method
    checkIdentify() {
        if (!this.returnIsIdentifyCallFinished()) {
            this.logIdentifyStep('identify_timeout', {
                expected_origins: this.getExpectedIdentifyOrigins(),
                iframe_present: !!document.getElementById('identify-frame')
            }, 'warn');
            this.cleanupIdentifyMessageHandler();
            this.onIdentifyFailure({
                code: "200",
                reference: '',
                reason: 'PRE ID FAILURE',
                status: IdentifyStatus.FAILED
            });
        }
    }
    handleIntegratorIframeIdentity() {
        //since heIframeIdentity has no error handled add a timeout then call identifyOnError
        this.iframeTimeout = window.setTimeout(() => {
            this.checkIdentify();
        }, this.identifyTimeout * 1000);
        Logger.info('iframe call started', {
            event_type: 'iframe_start'
        });
        this.logIdentifyStep('identify_iframe_request_start', {
            identify_timeout_seconds: this.identifyTimeout,
            identify_endpoint: this.identifyEndpoint,
            iframe_origin_candidates: this.getExpectedIdentifyOrigins()
        });
        this.integrator.customHeIframeIdentifyBaseUrl(this.identifyEndpoint, {
            returnUrl: window.location.href,
        });
    }
    iframeIdentify() {
        this.identifyStartedAt = Date.now();
        this.identifyFirstMessageAt = 0;
        this.logIdentifyStep('identify_bootstrap_start', {
            expected_origins: this.getExpectedIdentifyOrigins(),
            internal_call: this.internalCall
        });
        this.cleanupIdentifyMessageHandler();
        this.identifyMessageHandler = (event) => {
            const data = this.parseIframeMessageData(event);
            const response = data || event.data;
            const expectedOrigins = this.getExpectedIdentifyOrigins();
            const originMatched = expectedOrigins.length === 0 || expectedOrigins.includes(event.origin);
            if (!originMatched) {
                this.logIdentifyStep('identify_message_origin_mismatch', {
                    origin: event.origin,
                    expected_origins: expectedOrigins,
                    data: response
                }, 'warn');
                return;
            }
            if (!this.identifyFirstMessageAt) {
                this.identifyFirstMessageAt = Date.now();
                this.logIdentifyStep('identify_first_message_received', {
                    origin: event.origin,
                    expected_origins: expectedOrigins,
                    data: response
                });
            }
            this.cleanupIdentifyMessageHandler();
            this.handleStandardIdentifyResponse(response);
        };
        window.addEventListener('message', this.identifyMessageHandler);
        this.logIdentifyStep('identify_message_listener_attached', {
            expected_origins: this.getExpectedIdentifyOrigins()
        });
        if (!this.internalCall) {
            this.handleIntegratorIframeIdentity();
        }
    }
    ajaxIdentify() {
        if (!this.internalCall) {
            this.integrator.heIdentifyGetRequest({
                return: window.location.origin + window.location.pathname,
            }, this.handleStandardIdentifyResponse.bind(this), this.handleStandardIdentifyResponse.bind(this));
        }
    }
    getSuccessQueryParam() {
        return Utilities.getQueryParameter(null, 'success');
    }
    getIdentityQueryParam() {
        return Utilities.getQueryParameter(null, 'identity');
    }
    preIdentify() {
        const success = this.getSuccessQueryParam();
        const identity = this.getIdentityQueryParam();
        Logger.debug("PRE ID", {
            "success": success,
            "identity": identity
        });
        if (identity && success == '1') {
            this.identifyCallFinished = true;
            let alteredURL = Utilities.removeQueryParameter('identity', window.location.href);
            alteredURL = Utilities.removeQueryParameter('success', alteredURL);
            window.history.pushState({}, '', alteredURL);
            this.onIdentifySuccess({
                identity: identity,
                reference: '',
                status: IdentifyStatus.FAILED
            });
        }
        else {
            this.onIdentifyFailure({
                code: "200",
                reference: '',
                reason: 'PRE ID FAILURE',
                status: IdentifyStatus.FAILED
            });
        }
    }
    initialIdentifyLogic() {
        if (this.iframeTimeout) {
            window.clearTimeout(this.iframeTimeout);
        }
        this.cleanupIdentifyMessageHandler();
    }
    handleStandardIdentifyResponse(response) {
        var _a, _b;
        Logger.info('received iframe message event', {
            event_type: 'iframe_response',
            identified: 'identified' in response ? (_a = response.identified) !== null && _a !== void 0 ? _a : null : null,
            reference: 'reference' in response ? (_b = response.reference) !== null && _b !== void 0 ? _b : null : null,
            time_taken_ms: this.identifyStartedAt ? Date.now() - this.identifyStartedAt : null
        });
        this.logIdentifyStep('identify_response_received', {
            response
        });
        Logger.debug('[Flow Base] Handling IFRAME Response', { "response": response });
        //@ts-ignore
        if (typeof response.code !== 'undefined') {
            //@ts-ignore
            if (response.code != 200) {
                Logger.error('Bad identification call with non 200 status code', { "response": response });
                this.setFlowStep(FlowStep.IDENTIFY_FAILURE);
                this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
                return;
            }
        }
        //@ts-ignore
        if (typeof response.status === 'undefined') {
            Logger.warn('Unable to process identification call', { "response": response });
            this.setFlowStep(FlowStep.IDENTIFY_FAILURE);
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
            return;
        }
        //Unlike old system which relied on status_code to tell what had happened the new system will use status and phase out
        //status code as the source of truth for the requests
        //@ts-ignore
        switch (response.status) {
            case IdentifyStatus.IDENTIFIED:
                if (isSuccessResponse(response)) {
                    this.onIdentifySuccess(response);
                }
                else {
                    Logger.warn('Detected IDENTIFIED status but incorrect response', { "response": response });
                }
                break;
            case IdentifyStatus.IDENTIFIED_WITH_CONDITION:
                if (isSuccessWithConditionResponse(response)) {
                    this.onIdentifySuccessWithCondition(response);
                }
                else {
                    Logger.warn('Detected IDENTIFIED_WITH_CONDITION status but incorrect response', { "response": response });
                }
                break;
            case IdentifyStatus.FAILED:
                if (isFailureResponse(response)) {
                    this.onIdentifyFailure(response);
                }
                else {
                    Logger.warn('Detected ERROR status but incorrect response', { "response": response });
                }
                break;
            default:
                Logger.warn('Unable to process identification call', { "response": response });
                this.setFlowStep(FlowStep.IDENTIFY_FAILURE);
                this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
                break;
        }
    }
    onIdentifySuccessWithCondition(response) {
        if (response.conditions === null) {
            Logger.error('got ID success with conditions but no conditions given', { "response": response });
            this.onIdentifySuccess(response);
            return;
        }
        if (response.conditions.includes("already_subscribed")) {
            this.onIdentifySubscriptionExists(response);
        }
    }
    onIdentifyForbidden(response) {
        this.uiControl.displayMessage('FORBIDDEN_ERROR', '');
    }
    onIdentifySuccess(response) {
        this.initialIdentifyLogic();
        this.logIdentifyStep('identify_resolved', {
            outcome: 'success',
            reference: response.reference,
            identity: response.identity
        });
        Logger.debug('[Identity Success] id response', { "response": response });
        this.identity = response.identity;
        this.identified = true;
        this.tracker.track(Events.IDENTIFY_SUCCESS, {
            reference: response.reference,
        });
        this.identifyCallFinished = true;
        this.showFirstStep();
    }
    onIdentifyFailure(response) {
        this.logIdentifyStep('identify_resolved', {
            outcome: 'failure',
            reference: response.reference,
            reason: response.reason,
            status: response.status
        }, response.reason === 'non_subscriber' ? 'info' : 'warn');
        if (response.reason == "blocked") {
            this.onIdentifyForbidden(response);
        }
        else if (response.reason == 'non_subscriber') {
            this.initialIdentifyLogic();
            Logger.debug(`[Identity Failure] id response.`, { "response": response });
            this.tracker.track(Events.IDENTIFY_FAILURE, {
                reference: response.reference,
            });
            this.identifyCallFinished = true;
            this.identifyBackup();
        }
        else {
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
    }
    onOTPIdentifyFailure(response) {
        if (response.reason == "blocked") {
            this.onIdentifyForbidden(response);
        }
        else if (response.reason == 'non_subscriber') {
            this.initialIdentifyLogic();
            Logger.debug(`[OTP Identity Failure] id response.`, { "response": response });
            this.tracker.track(Events.IDENTIFY_FAILURE, {
                reference: response.reference,
            });
            this.setFlowStep(FlowStep.OTP_SUBMIT_ERROR);
        }
        else {
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
    }
    onIdentifySubscriptionExists(response) {
        var _a;
        this.initialIdentifyLogic();
        this.logIdentifyStep('identify_resolved', {
            outcome: 'already_subscribed',
            reference: response.reference,
            conditions: response.conditions
        });
        let access_token = '';
        if (typeof response.conditions_data === 'undefined' ||
            typeof response.conditions_data["already_subscribed"] === 'undefined' ||
            typeof response.conditions_data["already_subscribed"].token === 'undefined') {
            Logger.warn('Subscription exists without access token');
            //Still redirect so the user isn't lost
        }
        else {
            access_token = (_a = response.conditions_data["already_subscribed"].token) !== null && _a !== void 0 ? _a : '';
        }
        this.redirecting = true;
        // we set this field in all blocks to avoid isIdentifyCallFinished resolving before this.redirect is set
        this.identifyCallFinished = true;
        this.tracker.track(Events.SUBSCRIPTION_EXISTS, {});
        this.redirectToProduct(access_token);
    }
    identifyBackup() {
        Logger.debug('Call identify backup.', this.OTP);
        this.identifyBackupCalled = true; //OTP is called in the background behind the content click modal which must stay up
        if (this.OTP) {
            this.tracker.track(Events.OTP_CHALLENGE, {});
            this.setFlowStep(FlowStep.OTP_CHALLENGE);
        }
        else {
            //OTP not enabled just do WIFI
            this.tracker.track(Events.SHOW_WIFI, {});
            this.setFlowStep(FlowStep.WIFI);
            this.uiControl.showWifiFlowWithRefreshPage();
        }
    }
    shouldConditionalClickBeShown() {
        //Default just show conditional click but different flows can override this function and implement there own conditions
        this.setFlowStep(FlowStep.CONDITIONAL_CLICK);
    }
    showFirstStep() {
        if (this.conditionalClick && this.conditionalClickPlacement === ConditionalClickPlacement.FIRST) {
            this.shouldConditionalClickBeShown();
        }
        else if (this.subscribeClick) {
            this.setFlowStep(FlowStep.SUBSCRIBE);
        }
        else {
            this.setFlowStep(FlowStep.CONFIRM);
        }
    }
    subscribe() {
        this.setInactiveTimeout();
        this.tracker.track(Events.FIRST_CLICK, {});
        //check if conditional click is second and should go after subscribe
        if (this.conditionalClick && this.conditionalClickPlacement === ConditionalClickPlacement.SECOND) {
            this.shouldConditionalClickBeShown();
        }
        else {
            this.setFlowStep(FlowStep.CONFIRM);
        }
    } // overridable, this function simply needs to show the confirm button
    confirm() {
        const checkbox = this.uiControl.controls.consent.consentTickbox;
        if (!this.consentTickbox || (this.consentTickbox && checkbox.checked)) {
            this.clearInactiveTimeout();
            this.tracker.track(Events.SECOND_CLICK, {});
            this.handleSubscribe();
        }
    }
    handleConditionalClick() {
        this.setInactiveTimeout();
        this.tracker.track(Events.CONDITIONAL_CLICK, {});
        if (this.subscribeClick && this.conditionalClickPlacement === ConditionalClickPlacement.FIRST) { //conditional click was first so came before subscribe so show subscribe if enabled
            //if it is second then no check is needed confirm will always come after conditional click if conditional click was second
            this.setFlowStep(FlowStep.SUBSCRIBE);
        }
        else {
            this.setFlowStep(FlowStep.CONFIRM);
        }
    }
    returnIsIdentifyCallFinished() {
        return this.identifyCallFinished;
    }
    isIdentifyCallFinished() {
        // page is in redirect state; anything polling for ID call completion shouldn't happen
        return this.redirecting ? false : this.identifyCallFinished;
    }
    subscribeOnError(response) {
        this.uiControl.hideElement(this.uiControl.controls.spinner);
        if (response.error == "carrier") {
            this.uiControl.showErrorMessage("You are content blocked.Subscription failed.", "Retry");
            return;
        }
        if (response.code === 403) {
            Logger.error(`[Subscribe Error] Flow (${this.name}): fraud detected`, { "response": response });
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
            this.setFlowStep(FlowStep.FRAUD);
            this.dispatcher.dispatchFraudDetected({
                reference: response.reference,
                status_code: response.code,
                error: response.error || 'NO ERROR PROVIDED'
            });
        }
        else if (response.code === 402) {
            Logger.error(`[Subscribe Error] Flow (${this.name}): insufficent funds`, { "response": response });
            this.setFlowStep(FlowStep.SUBSCRIPTION_FAILED);
            this.dispatcher.dispatchSubscriptionFailed({
                reference: response.reference,
                status_code: response.code,
                error: response.error || 'NO ERROR PROVIDED',
            });
            this.uiControl.displayCustomMessage('You have insufficient funds. Please recharge and try again', '');
        }
        else {
            Logger.error(`[Subscribe Error] Flow (${this.name}): received error from integrator`, { "response": response });
            this.setFlowStep(FlowStep.SUBSCRIPTION_FAILED);
            this.dispatcher.dispatchSubscriptionFailed({
                reference: response.reference,
                status_code: response.code,
                error: response.error || 'NO ERROR PROVIDED',
            });
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
    }
    subscribeOnSuccess(response) {
        Logger.debug(`[Subscribe Success] Flow (${this.name}): received integrator response`, { "response": response });
        if (response.reason == "carrier") {
            this.uiControl.showErrorMessage("You are content blocked.Subscription failed.", "Retry");
            return;
        }
        if (response.code == 200 && response.status == "subscribed") {
            this.setFlowStep(FlowStep.SUBSCRIPTION_SUCCEEDED);
            if (response.new) {
                this.dispatcher.dispatchSubscriptionSucceeded({
                    jwt: response.access_token || '',
                    reference: response.reference,
                });
            }
            else {
                this.tracker.track(Events.SUBSCRIPTION_EXISTS, {
                    data: {
                        reference: response.reference,
                    },
                });
                if (typeof response.access_token === 'undefined') {
                    Logger.error('Subscribed without an access token', { "response": response });
                }
                this.redirectToProduct(response.access_token || '');
            }
        }
        else {
            this.setFlowStep(FlowStep.SUBSCRIPTION_FAILED);
            this.dispatcher.dispatchSubscriptionFailed({
                reference: response.reference,
                status_code: response.code,
                error: response.reason || 'NO ERROR PROVIDED',
            });
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
    }
    subscribeUser() {
        if (this.identified) {
            Logger.debug('[Subscribe User] user is identified; attempting subscription');
            const data = {
                metadata: this.metadata,
                subscription_data: this.metadata,
                marketing_data: this.metadata,
                identity: this.identity,
                fraud_transaction_id: this.metadata.fraud_detection_id || '',
                locale: this.locale,
                product: this.productSlug,
                plan_id: parseInt(this.plan),
                territory: this.territory,
                tracking_id: this.metadata.event_tracking_id || '',
            };
            this.integrator.customHeSubscribeRequest(data, this.subscribeEndpoint, this.subscribeOnSuccess.bind(this), this.subscribeOnError.bind(this));
        }
        else {
            this.identifyBackup();
        }
    }
    handleSubscribe() {
        if (this.preSubscribe) {
            this.dispatcher.dispatchPreSubscribe();
        }
        this.setFlowStep(FlowStep.SPINNER); //show spinner as soon as they press subscribe
        Utilities.waitForConditionAndExecute(this.returnIsIdentifyCallFinished.bind(this), this.subscribeUser.bind(this));
    }
    getProductRedirectUrl(baseUrl, pathname, params) {
        const urlParams = new URLSearchParams(window.location.search);
        let redirectUrl;
        if (urlParams.has('returnUrl')) {
            redirectUrl = new URL(urlParams.get('returnUrl') || "");
        }
        else {
            redirectUrl = new URL(baseUrl);
            if (pathname.includes('#')) {
                const [redirectPath, redirectHash] = pathname.split('#', 2);
                redirectUrl.pathname = redirectPath || '/';
                redirectUrl.hash = `#${redirectHash}`;
            }
            else {
                redirectUrl.pathname = pathname;
            }
        }
        if (this.metadata.event_tracking_id) {
            redirectUrl.searchParams.append('event_tracking_id', this.metadata.event_tracking_id);
        }
        redirectUrl.searchParams.append('locale', this.locale);
        for (var key in params) {
            if (typeof params[key] === 'string') {
                redirectUrl.searchParams.append(key, params[key]);
            }
        }
        return redirectUrl.href;
    }
    redirectToProduct(jwt) {
        // remove unload message
        window.onbeforeunload = null;
        let redirectPath = 'login';
        if (this.metadata['page_attributes-login_path']) {
            redirectPath = this.metadata['page_attributes-login_path'];
        }
        const redirectUrl = this.getProductRedirectUrl(this.productUrl, redirectPath, {
            auth: jwt
        });
        Logger.debug(`Flow (${this.name}): redirecting to product`, { "redirect_url": redirectUrl });
        this.tracker.track(Events.REDIRECT_TO_PRODUCT, {
            data: {
                redirectUrl: redirectUrl,
                withAuth: true
            },
        });
        window.location.assign(redirectUrl);
    }
    otpValidateSuccess(response) {
        Logger.debug(`[OTP Validate Success] Flow (${this.name}): received integrator response`, { "response": response });
        if (response.code == 200 && response.status == "subscribed") {
            this.setFlowStep(FlowStep.SUBSCRIPTION_SUCCEEDED);
            if (response.new) {
                this.dispatcher.dispatchSubscriptionSucceeded({
                    jwt: response.access_token || '',
                    reference: response.reference,
                });
            }
            else {
                this.tracker.track(Events.SUBSCRIPTION_EXISTS, {
                    data: {
                        reference: response.reference,
                    },
                });
                if (typeof response.access_token === 'undefined') {
                    Logger.error('Subscribed without an access token', { "response": response });
                }
                this.redirectToProduct(response.access_token || '');
            }
        }
        else {
            this.setFlowStep(FlowStep.SUBSCRIPTION_FAILED);
            this.dispatcher.dispatchSubscriptionFailed({
                reference: response.reference,
                status_code: response.code,
                error: response.reason || 'NO ERROR PROVIDED',
            });
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
    }
    otpValidateError(response) {
        Logger.error(`[OTP Validate] Flow (${this.name}): received error from integrator`, { "response": response });
        this.setFlowStep(FlowStep.OTP_VALIDATE_ERROR);
    }
    otpSubmitError(response) {
        Logger.error(`[OTP Submit Error] Flow (${this.name}): received error from integrator`, { "response": response });
        this.setFlowStep(FlowStep.OTP_SUBMIT_ERROR);
        this.tracker.track(Events.OTP_GENERATE_FAILURE, {
            data: {
                reference: response.reference,
            },
        });
    }
    onOTPSubmitWithCondition(response) {
        this.uiControl.displayMessage('412_FUNCTION_NOT_IMPLEMENTED', '');
    }
    onOTPSubmitForbiden(response) {
        this.uiControl.displayMessage('412_FUNCTION_NOT_IMPLEMENTED', '');
    }
    otpSubmitSuccess(response) {
        Logger.debug('Handling OTP Response', { "response": response });
        //@ts-ignore
        if (typeof response.code !== 'undefined') {
            //@ts-ignore
            if (response.code != 200) {
                Logger.error('Bad OTP call with non 200 status code', { "response": response });
                this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
                return;
            }
        }
        //@ts-ignore
        if (typeof response.status === 'undefined') {
            Logger.warn('Unable to process OTP call', { "response": response });
            this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
        }
        //Unlike old system which relied on status_code to tell what had happened the new system will use status and phase out
        //status code as the source of truth for the requests
        //@ts-ignore
        switch (response.status) {
            case IdentifyStatus.IDENTIFIED:
                if (isSuccessResponse(response)) {
                    this.identity = response.identity;
                    this.onOTPSubmitSuccess(response);
                }
                else {
                    Logger.warn('Detected IDENTIFIED status but incorrect response', { "response": response });
                }
                break;
            case IdentifyStatus.IDENTIFIED_WITH_CONDITION:
                if (isSuccessWithConditionResponse(response)) {
                    this.identity = response.identity;
                    this.onOTPSubmitWithCondition(response);
                }
                else {
                    Logger.warn('Detected IDENTIFIED_WITH_CONDITION status but incorrect response', { "response": response });
                }
                break;
            case IdentifyStatus.FAILED:
                if (isFailureResponse(response)) {
                    this.onOTPIdentifyFailure(response);
                }
                else {
                    Logger.warn('Detected ERROR status but incorrect response', { "response": response });
                }
                break;
            default:
                Logger.warn('Unable to process OTP call', { "response": response });
                this.uiControl.showErrorMessage('INTEGRATOR_ERROR', 'Retry');
                break;
        }
    }
    onOTPSubmitSuccess(response) {
        Logger.debug(`[OTP Submit Success] Flow (${this.name}): received integrator response`, { "response": response });
        // if(response.token){
        //     this.token = response.token;
        // }
        this.setFlowStep(FlowStep.OTP_VALIDATE);
        this.tracker.track(Events.OTP_GENERATE_SUCCESS, {
            data: {
                // code_token: response.token,
                reference: response.reference,
            },
        });
    }
    handleOtpChallengeValidate() {
        //After msisdn is submitted then the code is validated
        Logger.debug(`Flow (${this.name}): handling challenge validation`);
        this.setFlowStep(FlowStep.OTP_VALIDATE);
        if (this.preSubscribe) {
            this.dispatcher.dispatchPreSubscribe();
        }
        const otpCode = this.uiControl.controls.otp.code;
        const data = {
            metadata: this.metadata,
            subscription_data: {},
            marketing_data: {},
            locale: this.locale,
            product: this.productSlug,
            plan_id: parseInt(this.plan),
            territory: this.territory,
            identity: this.identity,
            fraud_transaction_id: this.fraud_detection_id,
            tracking_id: this.mixpanel_tracking_id,
            code: otpCode.value,
        };
        this.integrator.otpSubscribeCustomRequest(data, this.otpChallengeValidateURL, this.otpValidateSuccess.bind(this), this.otpValidateError.bind(this));
    }
    handleOtpChallengeSubmit() {
        //Submit the users msisdn
        this.setInactiveTimeout();
        this.setFlowStep(FlowStep.SPINNER);
        const otpMsisdn = this.uiControl.controls.otp.msisdn;
        if (otpMsisdn.value === '') {
            return;
        }
        var payload = {
            locale: this.locale,
            plan_id: parseInt(this.plan),
            msisdn: otpMsisdn.value
        };
        Logger.debug(`Flow (${this.name}): handling challenge submit`);
        this.setFlowStep(FlowStep.OTP_SUBMIT);
        this.integrator.otpGenerateCustomRequest(payload, this.optChallengeSubmitURL, this.otpSubmitSuccess.bind(this), this.otpSubmitError.bind(this));
    }
    consentTickboxTicked() {
        const checkbox = this.uiControl.controls.consent.consentTickbox;
        this.tracker.track(Events.CONSENT_TICKBOX_CHANGED, {
            data: {
                ticked: checkbox.checked
            }
        });
    }
    bindClickEvents() {
        this.dispatcher.addFirstClickListener(this.subscribe.bind(this));
        this.dispatcher.addSecondClickListener(this.confirm.bind(this));
        this.dispatcher.addConditionalClickListener(this.handleConditionalClick.bind(this));
        //OTP events are triggered in UIControl.js
        this.dispatcher.addOTPChallengeValidateListener(this.handleOtpChallengeValidate.bind(this));
        this.dispatcher.addOTPChallengeSubmitListener(this.handleOtpChallengeSubmit.bind(this));
        this.dispatcher.addConsentClickListener(this.consentTickboxTicked.bind(this));
        this.genericEvents.bindWifiClickEvent(this);
        this.genericEvents.bindFlowExitEvent(this);
    }
    handleSubscriptionFailed(event) {
        this.tracker.track(Events.SUBSCRIPTION_FAILED, event);
    }
    handleSubscriptionSucceeded(event) {
        this.tracker.track(Events.SUBSCRIPTION_SUCCEEDED, {
            data: {
                subscription: event.jwt,
                reference: event.reference,
            },
        });
        if (this.trackConversions) {
            const redirector = () => {
                this.redirectToProduct(event.jwt);
            };
            this.setFlowStep(FlowStep.SUBSCRIPTION_SUCCEEDED_CONTAINER);
            window.setTimeout(redirector.bind(this), 3500);
            this.uiControl.displayMessage('Please wait while you are redirected to the service', '');
            this.tracker.conversion({
                subscription: event.jwt,
            });
        }
        else {
            this.redirectToProduct(event.jwt);
        }
    }
    handleFraudDetected(event) {
        this.tracker.track(Events.FRAUD_DETECTED, {
            data: {
                reference: event.reference,
                status_code: event.status_code,
            },
        });
        this.handleSubscriptionFailed(event);
    }
    bindTrackingEvents() {
        this.dispatcher.addFraudDetectedListener(this.handleFraudDetected.bind(this));
        this.dispatcher.addSubscriptionFailedListener(this.handleSubscriptionFailed.bind(this));
        this.dispatcher.addSubscriptionSucceededListener(this.handleSubscriptionSucceeded.bind(this));
    }
    setFlowStep(flowStep) {
        Logger.debug(`Flow (${this.name}): changing step from ${this.step} to ${flowStep}`);
        this.step = flowStep;
        this.uiControl.setPageState(flowStep);
        //Some flowsteps are triggered in this flow for CSS purpose and therefore aren't included here. They can be added in the future though.
        switch (flowStep) {
            case FlowStep.SUBSCRIBE:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                this.uiControl.showElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                break;
            case FlowStep.CONFIRM:
                this.uiControl.hideElement(this.uiControl.controls.wifi);
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.showElement(this.uiControl.controls.confirm);
                this.uiControl.showElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.showElement(this.uiControl.controls.consent.consentTickboxMessage);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.tracker.track(Events.CTA_SHOWN, {});
                break;
            case FlowStep.CONDITIONAL_CLICK:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.showElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.showElement(this.uiControl.controls.conditionalClick.container);
                break;
            case FlowStep.OTP_CHALLENGE:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.otp.error);
                this.uiControl.showElement(this.uiControl.controls.otp.container);
                this.uiControl.showElement(this.uiControl.controls.otp.challenge);
                this.uiControl.showElement(this.uiControl.controls.otp.msisdn);
                this.uiControl.showElement(this.uiControl.controls.otp.msisdnText);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                break;
            case FlowStep.OTP_VALIDATE:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.otp.challenge);
                this.uiControl.hideElement(this.uiControl.controls.otp.error);
                this.uiControl.showElement(this.uiControl.controls.otp.validate);
                this.uiControl.hideElement(this.uiControl.controls.otp.msisdnText);
                this.uiControl.showElement(this.uiControl.controls.otp.validateText);
                this.uiControl.showElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.showElement(this.uiControl.controls.conditionalClick.container);
                break;
            case FlowStep.WIFI:
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.showElement(this.uiControl.controls.wifi);
                break;
            case FlowStep.OTP_SUBMIT:
                this.uiControl.showElement(this.uiControl.controls.spinner);
                break;
            case FlowStep.SPINNER:
                this.uiControl.showElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                break;
            case FlowStep.OTP_VALIDATE_ERROR:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.showElement(this.uiControl.controls.otp.error);
                break;
            case FlowStep.OTP_SUBMIT_ERROR:
                //Everything that happens in challenge
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                //But show the error instead
                this.uiControl.showElement(this.uiControl.controls.otp.error);
                break;
            case FlowStep.SUBSCRIPTION_SUCCEEDED_CONTAINER:
                this.uiControl.showElement(this.uiControl.controls.success.container);
                break;
            case FlowStep.FRAUD:
            case FlowStep.SUBSCRIPTION_FAILED:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                break;
            case FlowStep.TIMEOUT:
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickbox);
                this.uiControl.hideElement(this.uiControl.controls.consent.consentTickboxMessage);
                this.uiControl.hideElement(this.uiControl.controls.otp.container);
                break;
            case FlowStep.IDENTIFY_FAILURE:
                //Everything that happens in challenge
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.button);
                this.uiControl.hideElement(this.uiControl.controls.conditionalClick.container);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.otp.container);
                break;
            default:
                Logger.warn(`Flowstep ${flowStep} does not have a case and therefore there are no UI changes. Page Name: ${this.name}`);
                break;
        }
    }
}
